import { ITranslationKeys } from '@interfaces/ITranslationKeys';
import React from 'react';
import styled from '@emotion/styled';
import getUnixTime from 'date-fns/getUnixTime';
import { DeviceContext } from '../../../contexts/device/device';
import { LocaleConfigContext } from '../../../contexts/locale-config/locale-config';
import { getDetectedDevice } from '../../../utils/device-detection';
import { eventTracker } from '../../../utils/EventTracker';
import { numberFormat } from '../../../utils/formatting';
import { TranslationService, __ } from '../../../utils/translation-service';
import { DreamlinesButton } from '../../atoms/dreamlines-button/dreamlines-button';
import { SearchDropdown } from '../../molecules/search-dropdown/search-dropdown';
import { CalendarModal } from '../calendar-modal/calendar-modal';
import { LandingBreadcrumbs } from '../../molecules/landing-breadcrumbs/landing-breadcrumbs';
import Calendar, { IDates, DateTypes } from '../calendar/calendar';
import { IItem } from '../search-dropdown/common/interface';
import {
    StyledBestPrice,
    StyledContainer,
    StyledDesktopBagdeContainer,
    StyledDreamlinesPackageExpert,
    StyledHeadline,
    StyledHeadlineInner,
    StyledIconWithTextWrapper,
    StyledInnerContainer,
    StyledLabel,
    StyledPreHeadline,
    StyledSelectWrapper,
    StyledSsl,
    StyledThumbsUp,
    StyledUspContainer,
    StyledWinner,
} from './cruise-search-mask.style';
import { IState } from './interface';
import { reducer } from './reducer';
import { createSearchUrl, generateEventLabel } from './utils';
import { ICruiseSearchFacets } from '../../../../../interfaces/cruise';
import { transformFacetsData } from '../../molecules/cruise-search-mask/utils';
import { ISearchCruiseFacetsProps, searchCruiseFacets } from '../../../api/resources';
import CircularProgress from '@mui/material/CircularProgress';
import { IPageProps } from '@interfaces';

interface IIconWithText {
    icon: string;
    text: string;
}

interface IProps {
    preHeadline: string;
    headline: string;
    type: string;
    pageTypeId: number;
    uspIcons?: IIconWithText[];
    securityIcons?: IIconWithText[];
    cruiseSearchFacets: ICruiseSearchFacets;
    showBadge: boolean;
    showUSP?: boolean;
    containerMargin: string;
    pageProps: IPageProps;
}

const keys: ITranslationKeys[] = [
    'brandSearchHeadline',
    'regionSearchHeadline',
    'brandSelectAll',
    'regionSelectAll',
    'shipSelectAll',
    'searchPlaceHolder',
    'searchMaskUSP1',
    'searchMaskUSP2',
    'searchMaskUSP3',
    'weGuaranteeYouTheBestPrice',
    'yourDataIsSafeWithUs',
    'showOffersCTA',
];

TranslationService.registerKeys(keys);

const StyledSearchButton = styled(DreamlinesButton)`
    && {
        span:first-child {
            padding: 0;
        }
    }
`;

export type IDatesProps = IDates & { typeOfDate: DateTypes };

const getFacetsData = (payload: ISearchCruiseFacetsProps) => {
    return searchCruiseFacets(payload)
        .then((resp) => {
            const data = transformFacetsData(resp);
            return data;
        })
        .catch((err) => {
            console.error('Unable to retreive cruise information');
            return null;
        });
};

export const dateTypeEventMap = {
    startDate: 'calendar_departure',
    endDate: 'calendar_arrival',
};

export const CruiseSearchMask = ({
    preHeadline,
    type,
    headline,
    showBadge,
    showUSP = true,
    containerMargin,
    cruiseSearchFacets,
    pageTypeId,
    pageProps,
}: IProps) => {
    const device = React.useContext(DeviceContext);
    const localeConfig = React.useContext(LocaleConfigContext);
    const facetItems = transformFacetsData(cruiseSearchFacets);

    const currentShipItem = React.useMemo(() => {
        if (!facetItems?.shipItems?.all?.length) {
            return null;
        }
        return facetItems.shipItems.all.find((item) => Number(item.key) === pageTypeId) || null;
    }, [facetItems.shipItems?.all]);

    const initialState: IState = {
        regionItems: {
            top: facetItems.regionItems.top || [],
            all: facetItems.regionItems.all || [],
        },
        brandItems: { top: facetItems.brandItems.top || [], all: facetItems.brandItems.all || [] },
        shipItems: { top: facetItems.shipItems.top || [], all: facetItems.shipItems.all || [] },
        startDate: null,
        endDate: null,
        selectedRegion: null,
        selectedBrand: null,
        selectedShip: currentShipItem,
        totalCount: null,
        isLoading: false,
    };

    React.useEffect(() => {
        dispatch({
            type: 'SELECT_ITEM',
            selected: 'ship',
            selectedItem: initialState.selectedShip,
        });
        dispatch({ type: 'RECEIVE_DATA', data: facetItems });
    }, [cruiseSearchFacets]);

    const [state, dispatch] = React.useReducer(reducer, initialState);

    const {
        regionItems,
        startDate,
        endDate,
        selectedRegion,
        selectedBrand,
        selectedShip,
        totalCount,
        isLoading,
        shipItems,
    } = state;

    const selectedData = {
        region: selectedRegion,
        brand: selectedBrand,
        ship: selectedShip,
        startDate: startDate || null,
        endDate: endDate || null,
    };

    const searchButtonUrl = createSearchUrl(`/${localeConfig.slugs.search}`, selectedData);

    const submit = () => {
        const eventLabel = generateEventLabel(selectedData);

        eventTracker.track({
            event: 'click',
            label: eventLabel,
            action: 'search-button',
            category: `cruise-search-mask-${type}page`,
        });
    };

    const handleDatesChange = ({ startDate, endDate, typeOfDate }: IDatesProps) => {
        if (typeOfDate && (startDate || endDate)) {
            eventTracker.ga4Track('search_filters', {
                page_type: 'Ship Landing Page',
                event_type: dateTypeEventMap[typeOfDate],
            });
        }

        dispatch({ type: 'SELECT_DATES', selectedDates: { startDate, endDate } });

        getFacetsData({
            shipId: selectedShip?.key || pageTypeId,
            startDate: startDate ? getUnixTime(startDate) : undefined,
            endDate: endDate ? getUnixTime(endDate) : undefined,
            zoneId: selectedRegion?.key,
        }).then((data) => {
            if (data) {
                dispatch({ type: 'RECEIVE_DATA', data });
            }
        });
    };

    const handleItemChange = (item: IItem | null, type: 'brand' | 'region' | 'ship') => {
        const selectedItem = item?.key ? item : null;
        dispatch({ type: 'SELECT_ITEM', selected: type, selectedItem: selectedItem });

        const zoneId = type === 'region' ? item?.key : selectedRegion?.key;
        const shipId = type === 'ship' ? item?.key : selectedShip?.key;

        getFacetsData({
            shipId: shipId || pageTypeId,
            startDate: startDate ? getUnixTime(startDate) : undefined,
            endDate: endDate ? getUnixTime(endDate) : undefined,
            zoneId,
        }).then((data) => {
            if (data) {
                dispatch({ type: 'RECEIVE_DATA', data });
            }
        });
    };

    const isMobile = device === 'mobile';
    const isDesktop = device === 'desktop';

    return (
        <>
            <StyledContainer data-testid="cruise-search-mask-container" margin={containerMargin}>
                <StyledInnerContainer data-testid="cruise-search-mask-inner-container">
                    <StyledHeadlineInner>
                        <LandingBreadcrumbs pageProps={pageProps}></LandingBreadcrumbs>
                        <StyledPreHeadline>{preHeadline}</StyledPreHeadline>
                        <StyledHeadline>{headline}</StyledHeadline>
                    </StyledHeadlineInner>

                    <div>
                        <form onSubmit={(e) => e.preventDefault()}>
                            <StyledLabel>Search Cruise</StyledLabel>
                            <StyledSelectWrapper>
                                {type !== 'destination' && (
                                    <SearchDropdown
                                        onSelectHandler={(val) => handleItemChange(val, 'region')}
                                        items={regionItems}
                                        heading={__('regionSearchHeadline')}
                                        selectAllText={__('regionSelectAll')}
                                        placeholder={__('searchPlaceHolder')}
                                        selectedItem={selectedRegion}
                                        withModal={getDetectedDevice() === 'mobile'}
                                    />
                                )}
                                {type !== 'ship' && (
                                    <SearchDropdown
                                        onSelectHandler={(val) => handleItemChange(val, 'ship')}
                                        items={shipItems}
                                        placeholder={__('searchPlaceHolder')}
                                        heading={__('shipSelectAll')}
                                        selectAllText={__('shipSelectAll')}
                                        selectedItem={selectedShip}
                                        withModal={getDetectedDevice() === 'mobile'}
                                    />
                                )}
                            </StyledSelectWrapper>
                            <div style={{ width: '100%', marginBottom: '10px' }}>
                                {isMobile ? (
                                    <CalendarModal onDateChange={handleDatesChange} />
                                ) : (
                                    <Calendar onDateChange={handleDatesChange} />
                                )}
                            </div>
                            <StyledSearchButton
                                disabled={isLoading}
                                onClick={submit}
                                style={{
                                    width: '100%',
                                    marginTop: 10,
                                    height: 55,
                                    position: 'relative',
                                    cursor: 'pointer',
                                }}
                                wrapperStyle={{ width: '100%' }}
                                domType="a"
                                redirectUrl={searchButtonUrl}
                            >
                                {!!totalCount && !isLoading && (
                                    <span style={{ padding: '0' }}>{totalCount}</span>
                                )}
                                &nbsp;
                                {isLoading && (
                                    <CircularProgress
                                        thickness={6}
                                        size={18}
                                        sx={{
                                            color: '#fff',
                                            marginRight: '6px',
                                        }}
                                    />
                                )}
                                <span>{__('showOffersCTA')}</span>
                            </StyledSearchButton>
                        </form>

                        {!isMobile && (showUSP || isDesktop) && (
                            <StyledUspContainer>
                                <StyledIconWithTextWrapper>
                                    <StyledThumbsUp />
                                    <span>{__('searchMaskUSP2')}</span>
                                </StyledIconWithTextWrapper>
                                <StyledIconWithTextWrapper>
                                    <StyledDreamlinesPackageExpert />
                                    <span>{__('searchMaskUSP3')}</span>
                                </StyledIconWithTextWrapper>
                            </StyledUspContainer>
                        )}
                        {showUSP && isMobile && (
                            <StyledUspContainer>
                                <StyledWinner />
                                <span>{__('searchMaskUSP1')}</span>
                            </StyledUspContainer>
                        )}
                    </div>
                </StyledInnerContainer>

                {showBadge ? (
                    <StyledDesktopBagdeContainer>
                        <StyledIconWithTextWrapper>
                            <StyledBestPrice />
                            <span>{__('weGuaranteeYouTheBestPrice')}</span>
                        </StyledIconWithTextWrapper>
                        <StyledIconWithTextWrapper>
                            <StyledSsl />
                            <span>{__('yourDataIsSafeWithUs')}</span>
                        </StyledIconWithTextWrapper>
                    </StyledDesktopBagdeContainer>
                ) : (
                    // We always want an element to keep the search aligned
                    <span></span>
                )}
            </StyledContainer>
        </>
    );
};
